import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { MessageCircle, Paperclip, ClipboardList } from "lucide-react";
import { Task2 } from "@/types/tasks";

interface TasksTableViewProps {
  tasks: Task2[];
  onTaskSelect: (task: Task2) => void;
}

const statusConfig: Record<string, { label: string; color: string }> = {
  not_started: { label: "Não iniciado", color: "bg-gray-100 text-gray-800" },
  in_progress: { label: "Em execução", color: "bg-blue-100 text-blue-800" },
  waiting: { label: "Aguardando", color: "bg-yellow-100 text-yellow-800" },
  completed: { label: "Concluído", color: "bg-green-100 text-green-800" },
  cancelled: { label: "Cancelado", color: "bg-red-100 text-red-800" },
};

const priorityConfig: Record<string, { label: string; color: string }> = {
  low: { label: "Baixa", color: "bg-green-100 text-green-800" },
  medium: { label: "Média", color: "bg-yellow-100 text-yellow-800" },
  high: { label: "Alta", color: "bg-red-100 text-red-800" },
};

const formatDate = (date?: Date) =>
  date ? date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—";

export function TasksTableView({ tasks, onTaskSelect }: TasksTableViewProps) {
  if (tasks.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 flex flex-col items-center justify-center text-center text-muted-foreground gap-2">
          <ClipboardList className="h-8 w-8 opacity-40" />
          <span>Nenhuma tarefa encontrada. Crie a primeira em “Nova tarefa”.</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tarefa</TableHead>
                <TableHead>Responsável</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Prioridade</TableHead>
                <TableHead>Obra</TableHead>
                <TableHead>Setor</TableHead>
                <TableHead>Prazo</TableHead>
                <TableHead>Interações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tasks.map((task) => {
                const st = statusConfig[task.status] ?? statusConfig.not_started;
                const pr = priorityConfig[task.priority] ?? priorityConfig.medium;
                const assignee = task.assignees[0];
                return (
                  <TableRow
                    key={task.id}
                    className="hover:bg-muted/50 cursor-pointer"
                    onClick={() => onTaskSelect(task)}
                  >
                    <TableCell>
                      <p className="font-medium">{task.title}</p>
                      {task.description && (
                        <p className="text-sm text-muted-foreground line-clamp-1">{task.description}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      {assignee ? (
                        <div className="flex items-center gap-2">
                          <Avatar className="w-6 h-6">
                            <AvatarFallback className="text-xs">
                              {assignee.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm">{assignee.name}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell><Badge className={st.color}>{st.label}</Badge></TableCell>
                    <TableCell><Badge variant="outline" className={pr.color}>{pr.label}</Badge></TableCell>
                    <TableCell><span className="text-sm">{task.projectName}</span></TableCell>
                    <TableCell><span className="text-sm">{task.setor || "—"}</span></TableCell>
                    <TableCell>
                      <div className="text-sm whitespace-nowrap">
                        {task.endDate ? <>até {formatDate(task.endDate)}</> : "—"}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        {task.comments.length > 0 && (
                          <span className="flex items-center gap-1"><MessageCircle className="w-4 h-4" />{task.comments.length}</span>
                        )}
                        {task.attachments.length > 0 && (
                          <span className="flex items-center gap-1"><Paperclip className="w-4 h-4" />{task.attachments.length}</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
